import React, { useEffect, useState } from 'react';
import {
  EnterpriseModal,
  ModalField,
  ModalPrimaryButton,
  ModalSecondaryButton,
  modalInputClass,
  modalSelectClass,
} from '../layout/EnterpriseModal';
import type { OrgMember } from '../../hooks/useOrgMembers';
import { useCreateOpportunity } from '../../hooks/useOpportunities';
import {
  OPPORTUNITY_LEVELS,
  OPPORTUNITY_LEVEL_LABELS,
  type Opportunity,
  type OpportunityCreateInput,
  type OpportunityLevel,
} from '../../types/opportunity';

interface NewOpportunityModalProps {
  open: boolean;
  organizationId: string;
  createdBy?: string | null;
  members: OrgMember[];
  onClose: () => void;
  onCreated: (opportunity: Opportunity) => void;
  onError: (message: string) => void;
  origin?: Pick<
    OpportunityCreateInput,
    'title' | 'description' | 'source_kind' | 'source_issue_id' | 'source_label' | 'source_context'
  > | null;
  existingNotice?: string | null;
}

const EMPTY_LEVEL = '';

export const NewOpportunityModal: React.FC<NewOpportunityModalProps> = ({
  open,
  organizationId,
  createdBy,
  members,
  onClose,
  onCreated,
  onError,
  origin = null,
  existingNotice = null,
}) => {
  const createMutation = useCreateOpportunity(organizationId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [impact, setImpact] = useState<OpportunityLevel | ''>(EMPTY_LEVEL);
  const [effort, setEffort] = useState<OpportunityLevel | ''>(EMPTY_LEVEL);
  const [ownerId, setOwnerId] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle(origin?.title ?? '');
    setDescription(origin?.description ?? '');
    setCategory('');
    setImpact(EMPTY_LEVEL);
    setEffort(EMPTY_LEVEL);
    setOwnerId('');
  }, [open, origin?.source_issue_id, origin?.title, origin?.description]);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    try {
      const created = await createMutation.mutateAsync({
        title,
        description,
        category,
        impact_level: impact || null,
        effort_level: effort || null,
        owner_id: ownerId || null,
        created_by: createdBy,
        ...(origin ? {
          source_kind: origin.source_kind,
          source_issue_id: origin.source_issue_id,
          source_label: origin.source_label,
          source_context: origin.source_context,
        } : {}),
      });
      onCreated(created);
      onClose();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível capturar a oportunidade.');
    }
  };

  return (
    <EnterpriseModal
      open={open}
      onClose={onClose}
      title={origin?.source_kind === 'problem' ? 'Oportunidade de melhoria' : 'Nova oportunidade'}
      subtitle={origin?.source_kind === 'problem'
        ? 'A origem fica ligada a este problema. Nada é aprovado nem desdobrado agora.'
        : 'Registro manual. A origem fica como Manual.'}
      loading={createMutation.isPending}
      footer={
        <>
          <ModalSecondaryButton onClick={onClose} disabled={createMutation.isPending}>
            Cancelar
          </ModalSecondaryButton>
          <ModalPrimaryButton
            onClick={handleSubmit}
            disabled={!title.trim()}
            loading={createMutation.isPending}
          >
            Capturar
          </ModalPrimaryButton>
        </>
      }
    >
      {existingNotice ? (
        <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
          {existingNotice} Você ainda pode criar outra.
        </p>
      ) : null}
      {origin?.source_label ? (
        <p className="text-[12px] text-slate-600 dark:text-slate-300">{origin.source_label}</p>
      ) : null}
      <ModalField label="Título *">
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="O que pode melhorar?"
          className={modalInputClass}
          autoFocus
        />
      </ModalField>
      <ModalField label="Descrição">
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Contexto, evidência ou efeito esperado"
          className={`${modalInputClass} min-h-[80px] resize-y`}
          rows={3}
        />
      </ModalField>
      <ModalField label="Categoria">
        <input
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          placeholder="Ex.: qualidade, capacidade, custo"
          className={modalInputClass}
        />
      </ModalField>
      <div className="grid grid-cols-2 gap-3">
        <ModalField label="Impacto">
          <select
            value={impact}
            onChange={(event) => setImpact(event.target.value as OpportunityLevel | '')}
            className={modalSelectClass}
          >
            <option value="">Não informado</option>
            {OPPORTUNITY_LEVELS.map((level) => (
              <option key={level} value={level}>{OPPORTUNITY_LEVEL_LABELS[level]}</option>
            ))}
          </select>
        </ModalField>
        <ModalField label="Esforço">
          <select
            value={effort}
            onChange={(event) => setEffort(event.target.value as OpportunityLevel | '')}
            className={modalSelectClass}
          >
            <option value="">Não informado</option>
            {OPPORTUNITY_LEVELS.map((level) => (
              <option key={level} value={level}>{OPPORTUNITY_LEVEL_LABELS[level]}</option>
            ))}
          </select>
        </ModalField>
      </div>
      <ModalField label="Responsável">
        <select
          value={ownerId}
          onChange={(event) => setOwnerId(event.target.value)}
          className={modalSelectClass}
        >
          <option value="">Sem responsável</option>
          {members.map((member) => (
            <option key={member.id} value={member.id}>{member.name}</option>
          ))}
        </select>
      </ModalField>
    </EnterpriseModal>
  );
};
